import axios from 'axios'
import ls from 'local-storage'
import configuration from 'src/configuration'
import { notificationManager } from './notifications'
import { createDelegateJWTManager } from './delegateJWT'

/* Utility specifico per le API cash-planning che gestisce automaticamente l'autenticazione JWT delegata e gli header */

// Chiavi per il localStorage
const STORAGE_KEYS = {
  COMPANY_ID: 'cash_planning_company_id',
  USER_ID: 'cash_planning_user_id'
}

// Crea il manager JWT delegato per cash-planning
const jwtManager = createDelegateJWTManager('cash-planning', 'cash_planning')

// Funzione per creare gli headers specifici per cash-planning con JWT delegato
const createCashPlanningHeaders = async (additionalHeaders = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...additionalHeaders
  }

  try {
    // Ottieni un JWT delegato valido
    const delegateJWT = await jwtManager.getValidDelegateJWT()

    if (delegateJWT) {
      // Il delegateJWT è già il token stesso, non un oggetto nested
      headers.Authorization = `Bearer ${delegateJWT?.data?.token || delegateJWT}`
    }
  } catch (error) {
    console.error('Error getting delegate JWT for headers:', error)
    // Fallback al token principale se il JWT delegato fallisce
    const mainToken = ls.get('plsadv_token')
    if (mainToken) {
      headers.Authorization = mainToken
    }
  }

  const companyId = ls.get(STORAGE_KEYS.COMPANY_ID)
  // const userId = ls.get(STORAGE_KEYS.USER_ID)
  
  if (companyId) {
    headers['X-Company-Id'] = companyId
  }

  // if (userId) {
  //   headers['x-user-id'] = userId
  // }

  return headers
}

// Funzione per settare i valori di Company ID e User ID
export const setCashPlanningHeaders = (companyId, userId) => {
  if (companyId) {
    ls.set(STORAGE_KEYS.COMPANY_ID, companyId)
  }
  if (userId) {
    ls.set(STORAGE_KEYS.USER_ID, userId)
  }
}

// Funzione per ottenere i valori correnti degli header
export const getCashPlanningHeaders = () => {
  return {
    companyId: ls.get(STORAGE_KEYS.COMPANY_ID),
    userId: ls.get(STORAGE_KEYS.USER_ID)
  }
}

// Funzione per rimuovere gli header salvati
export const clearCashPlanningHeaders = () => {
  ls.remove(STORAGE_KEYS.COMPANY_ID)
  ls.remove(STORAGE_KEYS.USER_ID)
}

// Funzione per pulire il JWT delegato (da chiamare al logout)
export const clearDelegateJWT = jwtManager.clearDelegateJWT

// Funzione per pulire tutti i dati cash-planning
export const clearAllCashPlanningData = () => {
  clearCashPlanningHeaders()
  clearDelegateJWT()
}

// Funzione per forzare il refresh del JWT delegato
export const refreshDelegateJWT = jwtManager.refreshDelegateJWT

// Funzione per ottenere informazioni dal JWT delegato corrente
export const getDelegateJWTInfo = jwtManager.getDelegateJWTInfo

// Funzione per ottenere manualmente un JWT delegato valido (per uso esterno)
export const ensureDelegateJWT = jwtManager.ensureDelegateJWT

// Crea un'istanza axios configurata per le API cash-planning
const cashPlanningAxios = axios.create({
  baseURL: configuration.cashPlanningApiBaseUrl
})

// Interceptor per aggiungere automaticamente gli header a ogni richiesta
cashPlanningAxios.interceptors.request.use(
  async (config) => {
    try {
      const cashPlanningHeaders = await createCashPlanningHeaders()
      config.headers = {
        ...config.headers,
        ...cashPlanningHeaders
      }
      // Per il multipart lascia che axios imposti Content-Type con il boundary corretto
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        delete config.headers['Content-Type']
      }
      return config
    } catch (error) {
      console.error('Error setting up request headers:', error)
      return config
    }
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor per gestire le risposte
cashPlanningAxios.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Gestione degli errori globale
    console.error('Cash Planning API Error:', error)
    notificationManager.error(error?.message || 'Errore')
    
    // Se riceviamo un errore 401/403, potrebbe essere che il JWT delegato sia scaduto o non valido
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      console.warn('Authentication error, clearing delegate JWT')
      jwtManager.clearDelegateJWT()
    }
    
    return Promise.reject(error)
  }
)

// Funzioni per le operazioni CRUD.
// Nelle chiamate senza corpo data resta undefined: con null axios, visto il Content-Type JSON
// impostato dall'interceptor, invierebbe il testo "null", che il backend rifiuta con 400
const cashPlanningApi = {
  // GET request
  get: async (endpoint, options = {}) => {
    try {
      const response = await cashPlanningAxios.get(endpoint, {
        params: options.params,
        ...options
      })
      return options.fullResponse ? response : response.data
    } catch (error) {
      throw error
    }
  },

  // POST request
  post: async (endpoint, data, options = {}) => {
    try {
      const response = await cashPlanningAxios.post(endpoint, data, options)
      return options.fullResponse ? response : response.data
    } catch (error) {
      throw error
    }
  },

  // PUT request
  put: async (endpoint, data, options = {}) => {
    try {
      const response = await cashPlanningAxios.put(endpoint, data, options)
      return options.fullResponse ? response : response.data
    } catch (error) {
      throw error
    }
  },

  // DELETE request
  delete: async (endpoint, options = {}) => {
    try {
      const response = await cashPlanningAxios.delete(endpoint, options)
      return options.fullResponse ? response : response.data
    } catch (error) {
      throw error
    }
  },

  // PATCH request
  patch: async (endpoint, data, options = {}) => {
    try {
      const response = await cashPlanningAxios.patch(endpoint, data, options)
      return options.fullResponse ? response : response.data
    } catch (error) {
      throw error
    }
  }
}

export default cashPlanningApi 